import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Container, Grid, Typography, Link, IconButton } from '@mui/material';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faPhone } from '@fortawesome/free-solid-svg-icons';
import { faFacebook, faTwitter, faInstagram, faYoutube } from '@fortawesome/free-brands-svg-icons';

// Context
import { useI18n } from '../../context/I18nContext';

const StyledFooter = styled.footer`
  background-color: var(--dark-bg);
  color: var(--light-text);
  padding: 3rem 0 1rem;
  
  @media (max-width: 600px) {
    padding: 2.5rem 0 1rem;
  }
`;

const FooterLogo = styled.img`
  height: 60px;
  margin-bottom: 1rem;
`;

const FooterLink = styled(Link)`
  color: #FFFFFF !important;
  transition: var(--transition);
  display: block;
  margin-bottom: 0.8rem;
  font-size: 0.95rem;
  opacity: 0.9;
  font-weight: 400;
  
  &:hover {
    color: #4FC3F7 !important;
    transform: translateX(5px);
    opacity: 1;
  }
`;

const SocialIcon = styled(IconButton)`
  color: var(--light-text);
  margin-right: 1rem;
  transition: var(--transition);
  
  &:hover {
    color: #80CBC4;
    transform: translateY(-3px);
  }
`;

const ContactItem = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  
  svg {
    margin-right: 0.5rem;
  }
`;

const CopyrightText = styled(Typography)`
  text-align: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.9rem;
  
  @media (max-width: 600px) {
    margin-top: 1.5rem;
    padding-top: 1.2rem;
    font-size: 0.85rem;
  }
`;

const Footer: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  
  const year = new Date().getFullYear();
  
  const navItems = [
    { label: 'Início', path: '/' },
    { label: 'Sobre', path: '/about' },
    { label: 'Como Funciona', path: '/how-works' },
    { label: 'Cidades', path: '/cidades' },
    { label: 'Mapa', path: '/map' },
  ];
  
  return (
    <StyledFooter>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -2 }}>
          {/* Logo e descrição - para tablets e desktop */}
          <Box 
            sx={{ 
              width: { xs: '100%', md: '50%' }, 
              px: 2, 
              mb: { xs: 4, md: 0 }, 
              display: { xs: 'none', sm: 'block' } 
            }}
          >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Box>
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 'bold',
                      color: '#FFFFFF',
                      fontFamily: '"Montserrat", sans-serif',
                      letterSpacing: '1px'
                    }}
                  >
                    TU<span style={{ color: '#80CBC4' }}>Road</span>
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
                  Histórias que ligam pessoas a lugares no Brasil
                </Typography>
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <SocialIcon aria-label="Facebook">
                    <FontAwesomeIcon icon={faFacebook} />
                  </SocialIcon>
                  <SocialIcon aria-label="Twitter">
                    <FontAwesomeIcon icon={faTwitter} />
                  </SocialIcon>
                  <SocialIcon aria-label="Instagram">
                    <FontAwesomeIcon icon={faInstagram} />
                  </SocialIcon>
                  <SocialIcon aria-label="YouTube">
                    <FontAwesomeIcon icon={faYoutube} />
                  </SocialIcon>
                </Box>
              </Box>
            </motion.div>
          </Box>

          {/* Logo e descrição - apenas mobile */}
          <Box 
            sx={{ 
              width: '100%', 
              px: 2, 
              mb: 3, 
              display: { xs: 'block', sm: 'none' } 
            }}
          >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 'bold',
                      color: '#FFFFFF',
                      fontFamily: '"Montserrat", sans-serif',
                      letterSpacing: '1px'
                    }}
                  >
                    TU<span style={{ color: '#80CBC4' }}>Road</span>
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
                  Histórias que ligam pessoas a lugares no Brasil
                </Typography>
              </Box>
            </motion.div>
          </Box>
          
          {/* Links rápidos - apenas para tablets e desktop */}
          <Box 
            sx={{ 
              width: { xs: '100%', sm: '50%', md: '25%' }, 
              px: 2, 
              mb: { xs: 4, sm: 0 },
              display: { xs: 'none', sm: 'block' }
            }}
          >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2,
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  fontWeight: 600,
                  position: 'relative',
                  display: 'inline-block',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: '40px',
                    height: '2px',
                    backgroundColor: '#80CBC4'
                  }
                }}
              >
                Links Rápidos
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {navItems.map((item) => (
                  <Typography
                    key={item.path}
                    variant="body1"
                    sx={{
                      color: '#FFFFFF',
                      mb: 1.2,
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: '#80CBC4',
                        transform: 'translateX(5px)',
                      }
                    }}
                    onClick={() => navigate(item.path)}
                  >
                    {item.label}
                  </Typography>
                ))}
              </Box>
            </motion.div>
          </Box>
          
          {/* Contato */}
          <Box sx={{ width: { xs: '100%', sm: '50%', md: '25%' }, px: 2 }}>
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Typography variant="h6" sx={{ mb: { xs: 2.5, sm: 2 } }}>
                Fale Conosco
              </Typography>
              
              <ContactItem>
                <FontAwesomeIcon icon={faEnvelope} />
                <Link
                  href="mailto:contato@trigen.com.br"
                  underline="none"
                  sx={{
                    color: '#FFFFFF',
                    transition: 'color 0.3s ease',
                    '&:hover': { color: '#80CBC4' }
                  }}
                >
                  <Typography variant="body2">
                    contato@trigen.com.br
                  </Typography>
                </Link>
              </ContactItem>
              
              <ContactItem>
                <FontAwesomeIcon icon={faPhone} />
                <Link
                  href="tel:+5561992111681"
                  underline="none"
                  sx={{
                    color: '#FFFFFF',
                    transition: 'color 0.3s ease',
                    '&:hover': { color: '#80CBC4' }
                  }}
                >
                  <Typography variant="body2">
                    61-992111681
                  </Typography>
                </Link>
              </ContactItem>
              
              {/* Ícones de redes sociais para exibição mobile */}
              <Box sx={{ display: { xs: 'flex', sm: 'none' }, mt: 3, mb: 1, justifyContent: 'center' }}>
                <SocialIcon aria-label="Facebook">
                  <FontAwesomeIcon icon={faFacebook} />
                </SocialIcon>
                <SocialIcon aria-label="Twitter">
                  <FontAwesomeIcon icon={faTwitter} />
                </SocialIcon>
                <SocialIcon aria-label="Instagram">
                  <FontAwesomeIcon icon={faInstagram} />
                </SocialIcon>
                <SocialIcon aria-label="YouTube">
                  <FontAwesomeIcon icon={faYoutube} />
                </SocialIcon>
              </Box>
            </motion.div>
          </Box>
        </Box>
        
        <CopyrightText variant="body2">
          &copy; {year} TURoad. Todos os direitos reservados.
        </CopyrightText>
      </Container>
    </StyledFooter>
  );
};

export default Footer;
